import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { CSSTransition } from "react-transition-group";
import { X, RotateCcw, Search, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { getFunctions, httpsCallable } from "firebase/functions";
import inititializeFirebase from "../../services/firebase";
import { getProductsDetailById, SIZE_ORDER } from "services/supabaseSearch";
import { MiniButton } from "components/standardElements";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";

const functions = getFunctions(inititializeFirebase());
const getStockIssuesCallable = httpsCallable(functions, "getStockIssues");
const updateStockIssueStatusCallable = httpsCallable(
  functions,
  "updateStockIssueStatus",
);

/**
 * Right-side drawer listing currently-dismissed stock issues so the rep can
 * undo an old dismiss. Fetches on open (dismissed rows aren't in the
 * dashboard's active query). Group by project; each row has an Undismiss
 * button that flips the row back to `new` and removes it from the drawer.
 *
 * `onChange` receives the updated row so the parent can reconcile it into
 * its state in place — avoids the full dashboard reload that would reset
 * scroll position and feel jarring when the rep just wanted to undo one
 * row without losing their place.
 */
export default function DismissedDrawer({
  show,
  onClose,
  projectLookup,
  onChange,
}) {
  const nodeRef = useRef();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [issues, setIssues] = useState([]);
  const [productsById, setProductsById] = useState({});
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedProjects, setCollapsedProjects] = useState(() => new Set());

  // Fetch on open. Reset on close.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearchTerm("");
    setCollapsedProjects(new Set());
    (async () => {
      try {
        const { data } = await getStockIssuesCallable({
          statuses: ["dismissed"],
        });
        if (cancelled) return;
        setIssues(data.issues || []);
        const needed = Array.from(
          new Set((data.issues || []).map((i) => i.product_id)),
        );
        if (needed.length > 0) {
          const resolved = await getProductsDetailById(needed);
          if (!cancelled) setProductsById(resolved);
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const i of issues) {
      const key = i.firebase_project_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    }
    return Array.from(map.entries())
      .map(([firebaseId, rowIssues]) => {
        const meta = projectLookup?.[firebaseId];
        return {
          firebaseId,
          name: meta?.name || firebaseId,
          distributor: meta?.distributor || null,
          accountNumber: meta?.accountNumber || null,
          rep: meta?.rep || null,
          issues: rowIssues,
        };
      })
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
  }, [issues, projectLookup]);

  // Search covers project name / distributor / rep AND product itemNumber /
  // name. If the project-level fields match, every item under that project
  // shows. Otherwise, only items whose product matches are kept.
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return grouped;
    return grouped.flatMap((group) => {
      const projectFields = [group.name, group.distributor, group.rep]
        .filter(Boolean)
        .map((v) => v.toLowerCase());
      if (projectFields.some((v) => v.includes(term))) return [group];
      const matchingIssues = group.issues.filter((issue) => {
        const product = productsById[issue.product_id];
        const itemNumber = (product?.itemNumber || "").toLowerCase();
        const productName = (product?.name || "").toLowerCase();
        return itemNumber.includes(term) || productName.includes(term);
      });
      if (matchingIssues.length === 0) return [];
      return [{ ...group, issues: matchingIssues }];
    });
  }, [grouped, searchTerm, productsById]);

  const visibleCount = useMemo(
    () => filtered.reduce((sum, g) => sum + g.issues.length, 0),
    [filtered],
  );

  const toggleCollapse = (firebaseId) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(firebaseId)) next.delete(firebaseId);
      else next.add(firebaseId);
      return next;
    });
  };

  const handleUndismiss = async (issueId) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.add(issueId);
      return next;
    });
    try {
      const { data } = await updateStockIssueStatusCallable({
        id: issueId,
        status: "new",
      });
      // Locally drop the row from the drawer.
      setIssues((prev) => prev.filter((i) => i.id !== issueId));
      toast.success("Restored to active queue", { autoClose: 2000 });
      // Hand the updated row up so the parent can splice it into its
      // state in place — no full reload, no scroll-jump.
      onChange?.(data?.issue ?? null);
    } catch (e) {
      toast.error(`Undismiss failed: ${e.message || e}`);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(issueId);
        return next;
      });
    }
  };

  return ReactDOM.createPortal(
    <CSSTransition
      nodeRef={nodeRef}
      in={show}
      unmountOnExit
      timeout={{ enter: 0, exit: 280 }}
    >
      <Root ref={nodeRef} role="dialog" aria-modal="true">
        <Backdrop onClick={onClose} />
        <Sheet>
          <Header>
            <TitleBlock>
              <Title>Dismissed</Title>
              <Subtitle>
                {loading
                  ? "Loading…"
                  : issues.length === 0
                  ? "No dismissed issues"
                  : searchTerm
                  ? `${visibleCount} of ${issues.length} match "${searchTerm}"`
                  : `${issues.length} dismissed across ${grouped.length} project${grouped.length === 1 ? "" : "s"}`}
              </Subtitle>
            </TitleBlock>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              <X size={20} />
            </CloseButton>
          </Header>

          {issues.length > 0 && (
            <SearchBar>
              <SearchIcon>
                <Search size={14} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search project, item, or product…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearBtn
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </ClearBtn>
              )}
            </SearchBar>
          )}

          <Body>
            {error && (
              <EmptyState error>
                Failed to load: {String(error.message || error)}
              </EmptyState>
            )}
            {!error && !loading && issues.length === 0 && (
              <EmptyState>
                Nothing dismissed right now. Dismissed items will show up here
                so you can restore them.
              </EmptyState>
            )}
            {!error && !loading && issues.length > 0 && filtered.length === 0 && (
              <EmptyState>No dismissed items match "{searchTerm}".</EmptyState>
            )}
            {filtered.map((group) => {
              const collapsed = collapsedProjects.has(group.firebaseId);
              return (
              <GroupSection key={group.firebaseId}>
                <GroupHeader
                  type="button"
                  onClick={() => toggleCollapse(group.firebaseId)}
                  aria-expanded={!collapsed}
                >
                  <ChevronSlot aria-hidden>
                    {collapsed ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </ChevronSlot>
                  <GroupNameBlock>
                    <GroupName>{group.name}</GroupName>
                    <GroupMeta>
                      {[group.distributor, group.accountNumber, group.rep]
                        .filter(Boolean)
                        .join(" · ")}
                    </GroupMeta>
                  </GroupNameBlock>
                  <GroupCount>
                    {group.issues.length} dismissed
                  </GroupCount>
                </GroupHeader>
                {!collapsed && (
                <RowList>
                  {group.issues.map((issue) => {
                    const product = productsById[issue.product_id];
                    const itemNumber =
                      product?.itemNumber || issue.product_id;
                    const name = product?.name || "";
                    const color = product?.color || "";
                    const sortedSizes = [...(issue.oos_sizes || [])].sort(
                      (a, b) =>
                        (SIZE_ORDER[a] ?? 99) - (SIZE_ORDER[b] ?? 99),
                    );
                    return (
                      <DismissedRow key={issue.id}>
                        <Thumb>
                          {product?.imageURL ? (
                            <img
                              src={product.imageURL}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/st_placeholder.svg";
                              }}
                            />
                          ) : (
                            <img src="/st_placeholder.svg" alt="" />
                          )}
                        </Thumb>
                        <RowInfo>
                          <RowTitle>
                            <ItemNumber>{itemNumber}</ItemNumber>
                            {name && <ItemName>{name}</ItemName>}
                            {color && <ItemColor>({color})</ItemColor>}
                          </RowTitle>
                          {sortedSizes.length > 0 && (
                            <SizeList>
                              {sortedSizes.map((s) => (
                                <SizeTag key={s}>{s}</SizeTag>
                              ))}
                            </SizeList>
                          )}
                        </RowInfo>
                        <MiniButton
                          type="button"
                          onClick={() => handleUndismiss(issue.id)}
                          disabled={busyIds.has(issue.id)}
                        >
                          <ButtonIcon>
                            <RotateCcw size={12} />
                          </ButtonIcon>
                          Undismiss
                        </MiniButton>
                      </DismissedRow>
                    );
                  })}
                </RowList>
                )}
              </GroupSection>
              );
            })}
          </Body>
        </Sheet>
      </Root>
    </CSSTransition>,
    document.getElementById("root"),
  );
}

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
  overflow: hidden;

  &.enter-done {
    pointer-events: auto;
  }
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.25s ease-out;

  ${Root}.enter-done & {
    opacity: 1;
  }

  ${Root}.exit & {
    opacity: 0;
  }
`;

const Sheet = styled.div`
  position: relative;
  height: 100%;
  width: 62vw;
  max-width: 760px;
  min-width: 480px;
  background: ${COLORS.primaryInverted};
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);

  ${Root}.enter-done & {
    transform: translateX(0);
  }

  ${Root}.exit & {
    transform: translateX(100%);
  }

  @media (max-width: 640px) {
    width: 100vw;
    min-width: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid ${COLORS.secondary};
`;

const TitleBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${FONTSIZE.large};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Subtitle = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: lowercase;
  letter-spacing: 0.02em;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: ${COLORS.stormtechGrey};
  cursor: pointer;

  &:hover {
    background: ${COLORS.tertiary};
    color: ${COLORS.text};
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 22px 0;
  padding: 6px 10px;
  border: 1px solid ${COLORS.secondary};
  border-radius: 6px;
  background: ${COLORS.primaryInverted};
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${COLORS.stormtechOrange};
  }
`;

const SearchIcon = styled.span`
  display: inline-flex;
  color: ${COLORS.stormtechGrey};
`;

const SearchInput = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-size: ${FONTSIZE.small};
  color: ${COLORS.text};

  &::placeholder {
    color: ${COLORS.stormtechGrey};
  }
`;

const ClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: 0;
  color: ${COLORS.stormtechGrey};
  cursor: pointer;

  &:hover {
    color: ${COLORS.text};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 22px 24px;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  font-size: ${FONTSIZE.small};
  color: ${({ error }) => (error ? COLORS.alertRed : COLORS.stormtechGrey)};
`;

const GroupSection = styled.section`
  & + & {
    margin-top: 22px;
  }
`;

const GroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 4px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid ${COLORS.secondary};
  margin-bottom: 6px;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-bottom-color: ${COLORS.stormtechOrange};
  }
`;

const ChevronSlot = styled.span`
  display: inline-flex;
  color: ${COLORS.stormtechGrey};
  flex-shrink: 0;
`;

const GroupNameBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupName = styled.h3`
  margin: 0;
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const GroupMeta = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
`;

const GroupCount = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: lowercase;
  letter-spacing: 0.02em;
  flex-shrink: 0;
`;

const RowList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DismissedRow = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid ${COLORS.secondary};

  &:last-child {
    border-bottom: 0;
  }
`;

const Thumb = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  overflow: hidden;
  opacity: 0.75;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RowTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: ${FONTSIZE.small};
  color: ${COLORS.stormtechGrey};
`;

const ItemNumber = styled.span`
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
`;

const ItemName = styled.span`
  text-transform: uppercase;
  color: ${COLORS.stormtechGrey};
`;

const ItemColor = styled.span`
  color: ${COLORS.stormtechGrey};
  text-transform: uppercase;
  font-size: ${FONTSIZE.xsmall};
`;

const SizeList = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const SizeTag = styled.span`
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.stormtechGrey};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 3px;
  background: ${COLORS.tertiary};
`;

const ButtonIcon = styled.span`
  display: inline-flex;
  margin-right: 4px;
  vertical-align: middle;
`;
